/**
 * Greeting teaser — the little card that pops out next to the launcher
 * bubble before the visitor has clicked anything.
 *
 * It carries a short greeting plus a handful of tappable starter questions.
 * Clicking the card (or one of the questions) opens the panel; the questions
 * additionally pre-fill the first visitor message so the conversation starts
 * with substance instead of an empty thread.
 *
 * The teaser is purely presentational: it never talks to the network, so the
 * copy has to be supplied by the embed (see `InitOptions.teaser` in main.ts)
 * or a per-tenant default. Rendering it is pure DOM inside the closed
 * shadow root, same as the panel.
 */

import type { WidgetPosition } from './theme'

export interface TeaserController {
  /** Reveal the teaser. Safe to call twice; a no-op once hidden/removed. */
  show(): void
  /** Hide the teaser and cancel the auto-hide timer. */
  hide(): void
  /** Remove listeners, timer and DOM. */
  destroy(): void
}

export interface MountTeaserOptions {
  /** Short hello shown at the top of the card. */
  greeting: string
  /** Starter questions. Blank entries are dropped. */
  questions: ReadonlyArray<string>
  /** Launcher brand colour, reused for the button accents. */
  primary: string
  /** Corner the launcher is anchored to — the card follows it. */
  position: WidgetPosition
  /**
   * Dismiss the teaser automatically after this many ms. Pass 0 to keep it
   * up until the visitor acts on it.
   */
  autoHideMs?: number
  /**
   * Called when the visitor engages. `question` is set when they picked one
   * of the starter questions, and undefined when they hit the card itself.
   */
  onOpen(question?: string): void
}

const DEFAULT_AUTO_HIDE_MS = 15_000

export function mountTeaser(
  shadow: ShadowRoot,
  opts: MountTeaserOptions,
): TeaserController {
  const questions = (opts.questions ?? []).map((q) => q.trim()).filter((q) => q.length > 0)
  const greeting = opts.greeting.trim()
  // Nothing to say — do not mount an empty card over the page.
  if (greeting.length === 0 && questions.length === 0) {
    return { show: () => {}, hide: () => {}, destroy: () => {} }
  }

  const autoHideMs = opts.autoHideMs ?? DEFAULT_AUTO_HIDE_MS
  let destroyed = false
  let hideTimer: ReturnType<typeof setTimeout> | null = null

  const style = document.createElement('style')
  style.textContent = teaserCss(opts.primary, opts.position)
  shadow.appendChild(style)

  const card = document.createElement('div')
  card.className = 'pc-teaser'
  card.setAttribute('role', 'dialog')
  card.setAttribute('aria-label', greeting || 'Chat với chúng tôi')
  card.hidden = true

  if (greeting) {
    const text = document.createElement('p')
    text.className = 'pc-teaser-greeting'
    text.textContent = greeting
    card.appendChild(text)
  }

  if (questions.length > 0) {
    const list = document.createElement('div')
    list.className = 'pc-teaser-questions'
    for (const question of questions) {
      const button = document.createElement('button')
      button.type = 'button'
      button.className = 'pc-teaser-question'
      button.textContent = question
      button.addEventListener('click', (event) => {
        // Otherwise the click bubbles up to the card handler and the same
        // engagement fires twice.
        event.stopPropagation()
        engage(question)
      })
      list.appendChild(button)
    }
    card.appendChild(list)
  }

  // Clicking the card body opens the chat with no prefill.
  card.addEventListener('click', () => engage(undefined))

  const close = document.createElement('button')
  close.type = 'button'
  close.className = 'pc-teaser-close'
  close.setAttribute('aria-label', 'Đóng')
  close.textContent = '×'
  close.addEventListener('click', (event) => {
    // A dismissal is not an engagement — keep the panel shut.
    event.stopPropagation()
    hide()
  })
  card.appendChild(close)

  shadow.appendChild(card)

  function engage(question: string | undefined) {
    if (destroyed) return
    hide()
    opts.onOpen(question)
  }

  function show() {
    if (destroyed) return
    card.hidden = false
    if (autoHideMs > 0) {
      hideTimer = setTimeout(() => hide(), autoHideMs)
    }
  }

  function hide() {
    if (hideTimer !== null) {
      clearTimeout(hideTimer)
      hideTimer = null
    }
    if (destroyed) return
    card.hidden = true
  }

  return {
    show,
    hide,
    destroy() {
      if (destroyed) return
      destroyNode()
    },
  }

  function destroyNode() {
    if (hideTimer !== null) {
      clearTimeout(hideTimer)
      hideTimer = null
    }
    destroyed = true
    card.remove()
    style.remove()
  }
}

function teaserCss(primary: string, position: WidgetPosition): string {
  // The card sits directly above (or below, for top-*) the 56px launcher,
  // following the same --pc-lift the launcher uses on narrow viewports.
  let anchor: string
  switch (position) {
    case 'bottom-right':
      anchor = 'bottom: calc(86px + var(--pc-lift, 0px)); right: 0;'
      break
    case 'bottom-left':
      anchor = 'bottom: calc(86px + var(--pc-lift, 0px)); left: 0;'
      break
    case 'top-right':
      anchor = 'top: calc(86px + var(--pc-lift, 0px)); right: 0;'
      break
    case 'top-left':
      anchor = 'top: calc(86px + var(--pc-lift, 0px)); left: 0;'
      break
  }
  return `
:host { all: initial; }
.pc-teaser {
  position: fixed; ${anchor}
  width: 300px; max-width: calc(100vw - 40px);
  background: #fff; color: #1f2937;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.18);
  padding: 14px 14px 12px;
  font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
  font-size: 14px; line-height: 1.45;
  cursor: pointer;
  animation: pc-teaser-in 180ms ease-out;
}
.pc-teaser[hidden] { display: none; }
@keyframes pc-teaser-in {
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: none; }
}
.pc-teaser-greeting { margin: 0 22px 10px 0; }
.pc-teaser-questions { display: flex; flex-direction: column; gap: 6px; }
.pc-teaser-question {
  border: 1px solid ${primary};
  background: #fff;
  color: ${primary};
  border-radius: 10px;
  padding: 7px 10px;
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: background 120ms ease, color 120ms ease;
}
.pc-teaser-question:hover {
  background: ${primary};
  color: #fff;
}
.pc-teaser-close {
  position: absolute; top: 6px; right: 8px;
  background: transparent; border: 0;
  color: #6b7280; font-size: 20px; line-height: 1;
  cursor: pointer; padding: 0 4px;
}
.pc-teaser-close:hover { color: #1f2937; }
`
}
